import { css } from '@emotion/react';
import { usePropertyHeroForEditingScan, usePropertyHeroForOrgProperty, useScanEditingContextOrUndefined } from '@kaiizen/kai-pro-api-react-state';
import React from 'react';

import { UploadIndicatorIcon } from '../Icons';
import { TYPOGRAPHY2 } from '../Styles';
import { AddPhotoIcon } from '../Widgets/StyledIcons';

const HERO_IMAGE_CARD_STYLE = css`
  label: HERO_IMAGE_CARD_STYLE;
  display: block;
  width: 100%;
  aspect-ratio: 1.4;
  border-radius: 0;
  position: relative;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
`;

const HERO_CLICKABLE_STYLE = css`
  label: HERO_CLICKABLE_STYLE;
  cursor: pointer;
`;

const HERO_IMAGE_CARD_WITH_UPLOAD_STYLE = css`
  label: HERO_IMAGE_CARD_WITH_UPLOAD_STYLE;
  &:hover .add-photo-overlay {
    opacity: 1;
  }
`;

const ADD_PHOTO_OVERLAY_STYLE = css`
  label: ADD_PHOTO_OVERLAY_STYLE;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;
  gap: 8px;
`;

const ADD_PHOTO_OVERLAY_TEXT_STYLE = css`
  label: ADD_PHOTO_OVERLAY_TEXT_STYLE;
  ${TYPOGRAPHY2.small}
  color: white;
  text-align: center;
`;

const HERO_UPLOAD_INDICATOR_STYLE = css`
  label: HERO_UPLOAD_INDICATOR_STYLE;
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  pointer-events: none;
`;

const useHeroUri = (scanId: string | undefined, orgPropertyId: string | undefined) => {
  const scanEditingContext = useScanEditingContextOrUndefined();
  const scanHeroUri = usePropertyHeroForEditingScan(scanEditingContext?.scanRoot, scanId);
  const propertyHeroUri = usePropertyHeroForOrgProperty(orgPropertyId);
  return scanId ? scanHeroUri : propertyHeroUri;
};

export const HeroImageCard = ({
  scanId,
  orgPropertyId,
  onPhotoClick,
  onPhotoUpload,
  hasUploadedHero,
  photoSource,
  aspectRatio = 1.4,
}: {
  scanId?: string;
  orgPropertyId?: string;
  onPhotoClick?: () => void;
  onPhotoUpload?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /** When false, we show "Add" overlay (if onPhotoUpload) even if a fallback image (e.g. maps) is shown. When true, click opens viewer. */
  hasUploadedHero?: boolean;
  photoSource?: 'camera' | 'upload';
  aspectRatio?: number;
}) => {
  const heroUri = useHeroUri(scanId, orgPropertyId);
  const hasDisplayImage = heroUri != null;
  const treatAsUploaded = hasUploadedHero != null ? hasUploadedHero : hasDisplayImage;

  const canUploadPhoto = !treatAsUploaded && !!onPhotoUpload;
  const canViewPhoto = treatAsUploaded && !!onPhotoClick;
  const clickable = canViewPhoto || canUploadPhoto;
  return (
    <label
      css={[
        HERO_IMAGE_CARD_STYLE,
        canUploadPhoto && HERO_IMAGE_CARD_WITH_UPLOAD_STYLE,
        clickable && HERO_CLICKABLE_STYLE,
        css`background-image: url(${heroUri});`,
        aspectRatio ? css`aspect-ratio: ${aspectRatio};` : undefined,
      ]}
      onClick={canViewPhoto ? onPhotoClick : undefined}
    >
      {photoSource === 'upload' && (
        <div css={HERO_UPLOAD_INDICATOR_STYLE}>
          <UploadIndicatorIcon size={32} />
        </div>
      )}
      {canUploadPhoto && (
        <>
          <input
            type='file'
            accept='image/*'
            multiple
            onChange={onPhotoUpload}
            style={{ display: 'none' }}
          />
          <div className='add-photo-overlay' css={ADD_PHOTO_OVERLAY_STYLE}>
            <AddPhotoIcon color='white' width={32} height={32} />
            <div css={ADD_PHOTO_OVERLAY_TEXT_STYLE}>Add Curbside Photo</div>
          </div>
        </>
      )}
    </label>
  );
};
